import { getClientEnv } from "@/const";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo } from "react";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const authProvider = getClientEnv().authProvider;
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() || '/login' } =
    options ?? {};
  const utils = trpc.useUtils();

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      if (authProvider === 'custom') {
        // For custom auth, use tRPC logout (clears cookie server-side)
        await logoutMutation.mutateAsync();

        // Clear local user info
        localStorage.removeItem('manus-runtime-user-info');

        // Redirect to login with full page replace to prevent navigating back
        // into restricted admin pages via browser Back button after logout
        window.location.replace("/login");
      } else {
        // For manus auth, use tRPC
        await logoutMutation.mutateAsync();
      }
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      // Invalidate auth cache for both auth providers
      if (authProvider !== 'custom') {
        // For manus: invalidate tRPC cache
        utils.auth.me.setData(undefined, null);
        await utils.auth.me.invalidate();
      } else {
        // For custom: force refetch to clear user state
        await meQuery.refetch();
      }
    }
  }, [logoutMutation, utils, authProvider, redirectPath, meQuery]);

  const state = useMemo(() => {
    if (meQuery.data) {
      localStorage.setItem(
        "manus-runtime-user-info",
        JSON.stringify(meQuery.data)
      );
    }
    return {
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;

    // Use client-side navigation with replace to avoid pushing /login into history stack
    window.history.replaceState(null, '', redirectPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
